import { useSectionQuery } from "@/api/hooks";
import { useLibraryAssetsQuery, useProjectAdoptionsQuery } from "@/api/library";
import { lockReasonsByPath } from "@/lib/fieldState";
import { SectionAdoptionPanel } from "@/ui/adoptions/SectionAdoptionPanel";
import { Card, CardBody, CardHeader, CardTitle } from "@/ui/Card";
import { FieldAnchorProvider } from "@/ui/validation/FieldAnchorContext";
import { ValidationPanel } from "@/ui/validation/ValidationPanel";

import { PinConsequences, pinCardAssetIds } from "./PinConsequences";
import { SummaryContent } from "./SummaryContent";
import { SummaryGenerationPolicy } from "./SummaryGenerationPolicy";
import { SummaryStructure } from "./SummaryStructure";

/**
 * The Clinical Summary at `/projects/:projectId/config/summary` (#373).
 *
 * WHAT IT REPLACES. Every clinical slug renders `SectionDiagnostics` — the
 * generic body: the effective projection as a flat field grid, plus the
 * validation panel. For this slug that is not enough. The summary carries the
 * report a clinician actually signs, and what the project has ADOPTED changes
 * what that report does; neither fact was on any Project surface before this.
 * So the `summary` slug renders this instead, and every other slug keeps
 * `SectionDiagnostics` untouched.
 *
 * TWO SERVER-OWNED READS, JOINED AND PRESENTED. `GET /sections/summary` is the
 * composed effective summary; `GET /adoptions` is the project's pin set. Both
 * are composed by the Editor Service, and this component performs no clinical
 * composition of its own — it groups the projection by the addresses the
 * service supplied and names the pins from the catalog. Nothing on any path is
 * a fixture, and nothing is inferred from a value.
 *
 * WHY THE PREVIEW TARGET CANNOT DISTURB IT (#373 AC 2). Nothing here reads
 * `projectOverview`. The retarget mutation writes that key and invalidates
 * `previewCandidates`, and touches neither `section` nor `projectAdoptions` —
 * so a target change leaves this screen's content and pin set alone, without
 * this component doing anything to arrange that. Sourcing any of it from the
 * overview would break the criterion silently, which is why it does not.
 *
 * WHY THE VALIDATION PANEL STAYS. `SectionDiagnostics` renders it for every
 * slug, and #7's join — a finding resolving to the field it names — is
 * anchored by the fields this surface renders. Dropping it here would remove
 * diagnostics from one clinical section and quietly break that join for it;
 * this ticket replaces the summary's BODY, not its diagnostics.
 *
 * READ-ONLY THROUGHOUT. No adoption mutation, no control that writes: #372
 * owns adoption, upgrade, diff and safety-lock controls, and the two surfaces
 * share `useProjectAdoptionsQuery` rather than each holding a copy of the set.
 *
 * TWO ADOPTION VIEWS, ONE PIN SET (#443). This slug now carries both: the
 * section-scoped block above the summary body — what supplies THIS section, at
 * which version, in which state — and the whole-pin-set card below it, which is
 * #373's and lists everything the project pins with what each pin does. They
 * answer different questions and must never disagree about the answer they
 * share, so the same `adoptions` result is passed to both. Adding a second
 * `useProjectAdoptionsQuery` for the new block would observe the same key and
 * usually agree, which is worse than disagreeing openly: it would be right
 * until a refetch happened to land between two renders.
 */
export function ClinicalSummary({
  projectId,
  sectionLabel,
}: {
  projectId: string;
  sectionLabel: string;
}) {
  const sectionQuery = useSectionQuery(projectId, "summary");
  // The ONE adoptions read on this screen. Passed down rather than re-called,
  // so a consequence renderer cannot hold a second copy of the pin set.
  const adoptions = useProjectAdoptionsQuery(projectId);
  // Archived rows included: an archived asset keeps its existing pins by
  // contract, so excluding them would leave a real pin unnamed.
  const catalog = useLibraryAssetsQuery(true);
  // The projection is READ but no longer RENDERED as values (#488). It stays
  // because it is the only source of `lock.reason` for the locked-floor join
  // below, and because the group headings are derived from the service's own
  // addresses rather than invented here.
  const fields = sectionQuery.data?.fields ?? [];
  const lockReasonByPath = lockReasonsByPath(fields);

  return (
    // One provider per section surface, as `SectionDiagnostics` does: anchors
    // registered by the fields below are the only paths a finding can resolve
    // to, so leaving this screen removes its jump affordances rather than
    // leaving links pointing at unmounted controls.
    <FieldAnchorProvider>
      <div className="space-y-6">
        {/*
          #443: the SECTION-scoped adoption view — what supplies THIS section —
          around the summary body, exactly where the other six slugs carry it.
          It reads the same `adoptions` query the whole-pin-set card below
          reads, passed down rather than called again, so the two views answer
          different questions about one set instead of two copies of it.

          The two views also each open the shared library (#615), and a pinned
          Summary prompt is an adopted item HERE and a card BELOW. One asset is
          one `View in Clinical library` per page (#570), so the panel is told
          which pins the cards link — the cards' own rule, `pinCardAssetIds`,
          computed from the stored pin read — and withholds its item's link for
          exactly those, keeping every control: the cards decide nothing.

          #909: the panel renders `ProjectSectionPane`, so this section gets
          the same shell as the other seven — adoption action top-right, body,
          evidence in `Details` — instead of the `Card` whose title repeated
          the breadcrumb leaf. The Summary COMPOSER (#640, #862-#864, #915,
          #916) is a separate surface and unchanged by this ticket; what it
          gains is the `controls` slot to arrive in.
        */}
        <SectionAdoptionPanel
          projectId={projectId}
          section="summary"
          sectionLabel={sectionLabel}
          sectionQuery={sectionQuery}
          adoptions={adoptions}
          catalog={catalog}
          libraryLinkedItemIds={pinCardAssetIds(adoptions)}
        >
          {/*
            #488 removed this component's field-level join: it no longer
            renders the summary's values, so it has no use for `findings`. The
            findings themselves are unchanged and still rendered by the
            validation panel below.
          */}
          <SummaryContent projectId={projectId} sectionQuery={sectionQuery} />
        </SectionAdoptionPanel>

        <Card>
          <CardHeader>
            <CardTitle>What this project has adopted</CardTitle>
          </CardHeader>
          <CardBody>
            <PinConsequences
              projectId={projectId}
              adoptions={adoptions}
              catalog={catalog}
            />
          </CardBody>
        </Card>

        {/*
          #864's clinical generation policy, ITS OWN CARD and deliberately
          BEFORE the report structure below.

          The order is the argument again. What the engine PRODUCES is a
          clinical decision and belongs beside the clinical content above it;
          how the produced report is LAID OUT is the renderer's, and stays last.
          Putting the policy inside the composer card would have said the
          opposite — that suppressing secondary diagnostic indicators is one
          more visibility switch — which is exactly the conflation #864 exists
          to prevent: the sentence this policy governs is appended INSIDE the
          chief complaint after generation, so no section visibility can remove
          it, and no prompt wording can either.

          It shares nothing with the panel below. Its own endpoint, its own
          compare-and-set identity and its own query key, so a report-layout
          save cannot move it and this save cannot move a layout.
        */}
        <Card>
          <CardHeader>
            <CardTitle>Clinical generation policy</CardTitle>
          </CardHeader>
          <CardBody>
            <SummaryGenerationPolicy projectId={projectId} />
          </CardBody>
        </Card>

        {/*
          #640's panel, FOURTH and not first. Its presentation controls write
          only the dedicated renderer document; prompt pins and runtime values
          remain outside that request schema.

          The order is the argument. What this project pins is the page's
          subject and stays where #373 put it; the structure explains which
          layer each of those pins belongs to and what the runtime and
          presentation layers do instead, which is context for the two cards
          above rather than a replacement for either. The parent epic's
          Definition of Done says no huge raw prompt body may replace the
          compact adoption list, and the surest way to hold that is for the
          adoption list to keep its position and for the panel that could have
          grown into one to render identities only.

          It reads the SAME `adoptions` result the two cards above read, passed
          down rather than called again — the third view of one pin set on this
          screen, and the third to observe one query rather than hold a copy of
          it. Nothing else is shared: the registry is a service-wide document
          with its own cache entry, and this panel neither reads nor needs the
          section projection.
        */}
        <Card>
          <CardHeader>
            <CardTitle>Summary report structure</CardTitle>
          </CardHeader>
          <CardBody>
            <SummaryStructure
              adoptions={adoptions}
              projectId={projectId}
              // The SAME section projection the content card reads: which
              // exact pins supplied the Summary is its statement, and the
              // report structure resolves each section's effective source
              // from it rather than from the pin set alone.
              summaryPrompts={
                sectionQuery.data?.section === "summary"
                  ? sectionQuery.data.summaryPrompts
                  : undefined
              }
            />
          </CardBody>
        </Card>

        <ValidationPanel
          projectId={projectId}
          lockReasonByPath={lockReasonByPath}
          // ONE SENTENCE (#909), for the reason `SectionDiagnostics` records.
          description="Validation runs against the whole draft. A finding naming a value on this screen offers a jump to it."
        />
      </div>
    </FieldAnchorProvider>
  );
}
